/**
 * The announcement bar: one message at a time, stepped by arrows or a swipe.
 *
 * Modelled on trueclassictees.com's, measured 2026-08-15: a 28px strip in near-black with
 * 12px/700 white text centred between two arrows, one message visible, a 0.6s slide, and —
 * watched for eleven seconds — NO autoplay. It moves only when a shopper moves it. That is
 * worth keeping: a bar that rotates on its own moves the thing someone is reading.
 *
 * Theirs is Swiper. This is not, because a three-message strip does not need a carousel
 * library: the whole behaviour is a translated track, two buttons and a pointer threshold.
 *
 * MESSAGES is the whole content surface. Three placeholders ship here; replacing them is
 * the only edit this file should need, and one message is a valid configuration — the
 * arrows hide themselves rather than stepping between identical slides.
 */

export const MESSAGES = [
  "⚡️ Summer Vault Open: Buy 3 Get 2 Free",
  "Free Shipping with All Vault Orders",
  "Summer Vault closes Soon. While supplies last.",
];

export const ROOT_CLASS = "tbAnnounce";
/** Matches the measured reference; also the transition the track animates on. */
export const SLIDE_MS = 600;
/** Pointer travel, in px, that counts as a swipe rather than a tap. */
export const SWIPE_THRESHOLD = 40;

/** The index `step` places from `from`, wrapping in both directions. */
export function nextIndex(from, step, count) {
  if (!Number.isFinite(count) || count < 1) return 0;
  return ((from + step) % count + count) % count;
}

/**
 * The bar's markup. Built as a string in one place so the structure the CSS targets and the
 * structure the script wires are the same thing.
 */
function build(messages) {
  const root = document.createElement("div");
  root.className = ROOT_CLASS;
  root.setAttribute("role", "region");
  root.setAttribute("aria-label", "Announcements");

  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = `${ROOT_CLASS}Arrow ${ROOT_CLASS}Prev`;
  prev.setAttribute("aria-label", "Previous announcement");
  prev.innerHTML =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const next = prev.cloneNode(true);
  next.className = `${ROOT_CLASS}Arrow ${ROOT_CLASS}Next`;
  next.setAttribute("aria-label", "Next announcement");
  next.innerHTML = next.innerHTML.replace("M15 5l-7 7 7 7", "M9 5l7 7-7 7");

  const viewport = document.createElement("div");
  viewport.className = `${ROOT_CLASS}Viewport`;

  const track = document.createElement("div");
  track.className = `${ROOT_CLASS}Track`;
  for (const text of messages) {
    const slide = document.createElement("p");
    slide.className = `${ROOT_CLASS}Slide`;
    slide.textContent = text;
    track.appendChild(slide);
  }
  viewport.appendChild(track);
  root.append(prev, viewport, next);
  return { root, track, prev, next };
}

/**
 * Wires stepping, swiping and the accessible state onto a built bar.
 * Exported for the same reason nextIndex is: the arithmetic is testable, the DOM is not.
 */
function wire({ root, track, prev, next }, count) {
  let index = 0;

  const render = () => {
    track.style.transform = `translateX(${-index * 100}%)`;
    // Only the visible message is exposed; the others are not read out or focusable.
    [...track.children].forEach((slide, i) =>
      slide.toggleAttribute("aria-hidden", i !== index),
    );
    root.dataset.index = String(index);
  };

  const step = (by) => {
    index = nextIndex(index, by, count);
    render();
  };

  prev.addEventListener("click", () => step(-1));
  next.addEventListener("click", () => step(1));

  // Swipe. Pointer events cover touch, pen and a mouse drag with one code path; the
  // threshold is what keeps a tap on an arrow from also registering as a swipe.
  let startX = null;
  root.addEventListener("pointerdown", (e) => {
    startX = e.clientX;
  });
  root.addEventListener("pointerup", (e) => {
    if (startX == null) return;
    const travel = e.clientX - startX;
    startX = null;
    if (Math.abs(travel) >= SWIPE_THRESHOLD) step(travel < 0 ? 1 : -1);
  });
  root.addEventListener("pointercancel", () => {
    startX = null;
  });

  render();
}

/**
 * Inserts the bar above the theme header, once.
 *
 * Above the header rather than inside it: the header is Prestige's own section and a
 * Prestige update replaces it, which would take an injected child with it.
 */
export function mount(messages = MESSAGES, doc = document) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  if (doc.querySelector(`.${ROOT_CLASS}`)) return null;

  const header =
    doc.querySelector("header.shopify-section") ?? doc.querySelector("header");
  if (!header?.parentElement) return null;

  const parts = build(messages);
  if (messages.length < 2) parts.root.dataset.single = "true";
  header.parentElement.insertBefore(parts.root, header);
  wire(parts, messages.length);
  return parts.root;
}

if (typeof document !== "undefined") {
  const start = () => mount();
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
